import { Button, Col, Form, FormInstance, Input, Row, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { formRules } from 'constants/GlobalConfig';

/** IP フィルターフォームのアイテム型 */
export type IpFilterFormItem = {
    setting: string;
    comment: string;
};

interface IpListFieldProps {
    /** Form.List に使用するフィールド名 */
    listName: string;
    /** 親フォームのインスタンス */
    form: FormInstance;
    /** ラベルなしのオフセットレイアウト */
    layoutWithoutLabel: object;
    /** 追加ボタンのレイアウト */
    layoutWithAddButton: object;
}

/**
 * IP アドレスリスト入力フィールド (再利用可能コンポーネント)
 * IP アドレスとコメントのペアを動的に追加・削除できる
 */
const IpListField: React.FC<IpListFieldProps> = ({
    listName,
    form,
    layoutWithoutLabel,
    layoutWithAddButton,
}) => {
    // リストの値を監視してバリデーションルールに使用する
    const listValues: IpFilterFormItem[] = Form.useWatch(listName, form) ?? [];

    /** 許可 IP リストのラベル */
    const listLabel = '許可 IP リスト';

    /** 許可 IP リストのツールチップ */
    const listTooltip = (
        <span>
            IPアドレスの詳しい登録方法は、
            <Typography.Link
                href='https://www.info.l-gate.net/admin_manual/'
                target='_blank'
                style={{ color: 'white', textDecoration: 'underline' }}
                onClick={(event) => event.stopPropagation()}
            >
                管理者マニュアル
            </Typography.Link>
            をご参照ください。
        </span>
    );

    return (
        <Form.List name={listName}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name }) => (
                        <Form.Item
                            key={key}
                            label={name === 0 ? listLabel : ''}
                            tooltip={name === 0 ? listTooltip : undefined}
                            {...(name === 0 ? {} : layoutWithoutLabel)}
                        >
                            <Row gutter={8} align='middle'>
                                <Col span={11}>
                                    <Form.Item
                                        name={[name, 'setting']}
                                        noStyle
                                        rules={[
                                            {
                                                // コメントが入力されている場合は IP アドレスを必須とする
                                                message:
                                                    'IP アドレスを入力してください',
                                                required:
                                                    (listValues[name]
                                                        ?.comment ?? '') !== '',
                                            },
                                            formRules.range({
                                                label: 'IP アドレス',
                                                max: 10000,
                                            }),
                                        ]}
                                    >
                                        <Input
                                            placeholder='IP アドレス'
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={11}>
                                    <Form.Item
                                        name={[name, 'comment']}
                                        noStyle
                                        rules={[
                                            formRules.range({
                                                label: 'コメント',
                                                max: 1000,
                                            }),
                                        ]}
                                    >
                                        <Input
                                            placeholder='コメント'
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={2}>
                                    <Button
                                        className='mr-2'
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(name)}
                                        shape='circle'
                                        size='small'
                                    />
                                </Col>
                            </Row>
                        </Form.Item>
                    ))}

                    {/* 追加ボタン (リストが空の場合はラベル・ツールチップを表示する) */}
                    <Form.Item
                        label={fields.length === 0 ? listLabel : ''}
                        tooltip={fields.length === 0 ? listTooltip : undefined}
                        {...(fields.length === 0 ? {} : layoutWithAddButton)}
                    >
                        <Button
                            block
                            icon={<PlusOutlined />}
                            onClick={() => add()}
                            type='dashed'
                        />
                    </Form.Item>
                </>
            )}
        </Form.List>
    );
};

export default IpListField;
